/**
 * Shape primitives, expressed as SVG path data.
 *
 * Everything the renderer draws goes through these functions, which means a
 * shape is defined exactly once and both output backends consume the same
 * geometry: the canvas wraps each string in a `Path2D`, the exporter writes it
 * straight into a `<path d="...">`. That is what makes real vector export
 * possible without a second implementation of every shape.
 *
 * Winding convention: solid shapes are emitted clockwise, holes
 * counter-clockwise (pass `reverse`). Sub-paths therefore compose under the
 * default nonzero fill rule — overlapping petals union instead of cancelling,
 * and a reversed sub-path punches a hole. No caller needs to pick a fill rule.
 */

/** Trims float noise so exported SVG stays small and diffable. */
export function n(value: number): string {
  return (Math.round(value * 1000) / 1000).toString();
}

export function rect(x: number, y: number, width: number, height: number, reverse = false): string {
  const corners: (readonly [number, number])[] = [
    [x, y],
    [x + width, y],
    [x + width, y + height],
    [x, y + height],
  ];
  return polygon(corners, reverse);
}

export function circle(cx: number, cy: number, r: number, reverse = false): string {
  if (r <= 0) return '';
  const sweep = reverse ? 0 : 1;
  // Two arcs, because a single 360° arc is degenerate in SVG path syntax.
  return (
    `M${n(cx - r)} ${n(cy)}` +
    `A${n(r)} ${n(r)} 0 1 ${String(sweep)} ${n(cx + r)} ${n(cy)}` +
    `A${n(r)} ${n(r)} 0 1 ${String(sweep)} ${n(cx - r)} ${n(cy)}Z`
  );
}

export function polygon(points: readonly (readonly [number, number])[], reverse = false): string {
  if (points.length === 0) return '';
  const ordered = reverse ? [...points].reverse() : points;
  const [first, ...rest] = ordered as [readonly [number, number], ...(readonly [number, number])[]];
  const head = `M${n(first[0])} ${n(first[1])}`;
  const tail = rest.map(([x, y]) => `L${n(x)} ${n(y)}`).join('');
  return `${head}${tail}Z`;
}

/**
 * Rounded rectangle with independently controllable corners.
 * `radius` is clamped so opposite corners can never overlap.
 */
export function roundedRect(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  corners: { tl?: boolean; tr?: boolean; br?: boolean; bl?: boolean } = {},
): string {
  const { tl = true, tr = true, br = true, bl = true } = corners;
  const r = Math.max(0, Math.min(radius, Math.min(width, height) / 2));
  if (r === 0) return rect(x, y, width, height);

  const right = x + width;
  const bottom = y + height;
  const arc = (ex: number, ey: number): string => `A${n(r)} ${n(r)} 0 0 1 ${n(ex)} ${n(ey)}`;

  let d = `M${n(tl ? x + r : x)} ${n(y)}`;
  d += `L${n(tr ? right - r : right)} ${n(y)}`;
  if (tr) d += arc(right, y + r);
  d += `L${n(right)} ${n(br ? bottom - r : bottom)}`;
  if (br) d += arc(right - r, bottom);
  d += `L${n(bl ? x + r : x)} ${n(bottom)}`;
  if (bl) d += arc(x, bottom - r);
  d += `L${n(x)} ${n(tl ? y + r : y)}`;
  if (tl) d += arc(x + r, y);
  return `${d}Z`;
}

/** Regular polygon inscribed in a circle. `rotation` is in radians. */
export function regularPolygon(
  cx: number,
  cy: number,
  radius: number,
  sides: number,
  rotation = 0,
  reverse = false,
): string {
  const points: (readonly [number, number])[] = [];
  for (let i = 0; i < sides; i++) {
    const angle = (i * 2 * Math.PI) / sides + rotation;
    points.push([cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius]);
  }
  return polygon(points, reverse);
}

/** Alternating outer/inner vertices — used for stars, flowers and gears. */
export function starPolygon(
  cx: number,
  cy: number,
  outerRadius: number,
  innerRadius: number,
  points: number,
  rotation = -Math.PI / 2,
): string {
  const vertices: (readonly [number, number])[] = [];
  for (let i = 0; i < points * 2; i++) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const angle = (i * Math.PI) / points + rotation;
    vertices.push([cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius]);
  }
  return polygon(vertices);
}

/** Symmetric heart centred on (cx, cy), `size` being its full height. */
export function heart(cx: number, cy: number, size: number): string {
  const w = size / 2;
  const top = cy - size * 0.35;
  const bottom = cy + size * 0.5;
  return (
    `M${n(cx)} ${n(bottom)}` +
    `C${n(cx - w)} ${n(cy + size * 0.05)} ${n(cx - w)} ${n(top - size * 0.15)} ${n(cx)} ${n(top)}` +
    `C${n(cx + w)} ${n(top - size * 0.15)} ${n(cx + w)} ${n(cy + size * 0.05)} ${n(cx)} ${n(bottom)}Z`
  );
}

/** Pointed-oval leaf on the top-left / bottom-right diagonal. */
export function leaf(cx: number, cy: number, size: number): string {
  const r = size / 2;
  return (
    `M${n(cx - r)} ${n(cy + r)}` +
    `Q${n(cx - r)} ${n(cy - r)} ${n(cx + r)} ${n(cy - r)}` +
    `Q${n(cx + r)} ${n(cy + r)} ${n(cx - r)} ${n(cy + r)}Z`
  );
}

/** Equal-armed cross; `thickness` is the arm width. */
export function cross(cx: number, cy: number, size: number, thickness: number): string {
  const half = size / 2;
  const t = thickness / 2;
  return polygon([
    [cx - t, cy - half],
    [cx + t, cy - half],
    [cx + t, cy - t],
    [cx + half, cy - t],
    [cx + half, cy + t],
    [cx + t, cy + t],
    [cx + t, cy + half],
    [cx - t, cy + half],
    [cx - t, cy + t],
    [cx - half, cy + t],
    [cx - half, cy - t],
    [cx - t, cy - t],
  ]);
}

/** Lightning bolt inscribed in a square of `size`. */
export function lightning(cx: number, cy: number, size: number): string {
  const w = size / 2;
  const h = size / 2;
  return polygon([
    [cx + w * 0.3, cy - h],
    [cx - w * 0.6, cy + h * 0.15],
    [cx - w * 0.05, cy + h * 0.15],
    [cx - w * 0.3, cy + h],
    [cx + w * 0.6, cy - h * 0.15],
    [cx + w * 0.05, cy - h * 0.15],
  ]);
}

/** Closed sine-modulated blob — a circle with `lobes` bumps. */
export function wave(
  cx: number,
  cy: number,
  radius: number,
  lobes: number,
  amplitude: number,
  steps = 72,
): string {
  const points: (readonly [number, number])[] = [];
  for (let i = 0; i < steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    const r = radius + Math.sin(lobes * angle) * amplitude;
    points.push([cx + Math.cos(angle) * r, cy + Math.sin(angle) * r]);
  }
  return polygon(points);
}

/**
 * Flower built from `petals` overlapping circles plus a centre disc.
 * All sub-paths wind the same way, so nonzero unions them into one blossom.
 */
export function flower(cx: number, cy: number, radius: number, petals: number): string {
  const petalRadius = radius * 0.45;
  const orbit = radius - petalRadius;
  let d = circle(cx, cy, petalRadius * 0.9);
  for (let i = 0; i < petals; i++) {
    const angle = (i * 2 * Math.PI) / petals;
    d += circle(cx + Math.cos(angle) * orbit, cy + Math.sin(angle) * orbit, petalRadius);
  }
  return d;
}

/** Heraldic shield: flat top, curved shoulders, pointed base. */
export function shield(cx: number, cy: number, width: number, height: number): string {
  const w = width / 2;
  const top = cy - height / 2;
  const bottom = cy + height / 2;
  return (
    `M${n(cx - w)} ${n(top)}` +
    `H${n(cx + w)}` +
    `V${n(cy)}` +
    `Q${n(cx + w)} ${n(bottom - height * 0.1)} ${n(cx)} ${n(bottom)}` +
    `Q${n(cx - w)} ${n(bottom - height * 0.1)} ${n(cx - w)} ${n(cy)}Z`
  );
}

/**
 * Deterministic pseudo-random value in [0, 1) for a module position.
 * Seeded by grid coordinates rather than pixel coordinates so a bubble keeps
 * its size when the symbol is re-rendered at a different scale.
 */
export function positionalNoise(row: number, col: number): number {
  const hash = Math.sin(row * 12.9898 + col * 78.233) * 43758.5453;
  return hash - Math.floor(hash);
}
